import { apiFetch } from "./client";

export interface Phone {
  id: string;
  name: string;
  device_id: string;
  connection_url: string;
  status: "online" | "offline" | "busy" | "error";
  // computed_status peut valoir 'paused' quand le run actif du tel est en pause
  // (le tel reste 'busy' en DB). Dérivé côté backend.
  computed_status: "online" | "offline" | "busy" | "error" | "paused";
  active_run_status?: "running" | "dispatched" | "paused" | null;
  active_run_id?: string | null;
  last_heartbeat_at: string | null;
  os_version: string | null;
  model: string | null;
  spoofed_model: string | null;
  current_proxy: { ip?: string; label?: string; asn?: string } | null;
  /** .env-style dict injecté dans tous les script runs comme `params`. */
  env_vars: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface PhoneCreateResponse {
  success: boolean;
  phone: Phone;
  credentials: { device_id: string; api_token: string };
  enrollment_command: string;
}

interface ListResponse {
  success: boolean;
  phones: Phone[];
}

interface SingleResponse {
  success: boolean;
  phone: Phone;
}

export async function listPhones(): Promise<Phone[]> {
  const data = await apiFetch<ListResponse>("/api/phones");
  return data.phones;
}

export async function getPhone(id: string): Promise<Phone> {
  const data = await apiFetch<SingleResponse>(`/api/phones/${id}`);
  return data.phone;
}

export async function createPhone(payload: {
  name: string;
  connection_url: string;
  spoofed_model?: string;
}): Promise<PhoneCreateResponse> {
  return apiFetch<PhoneCreateResponse>("/api/phones", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updatePhone(
  id: string,
  payload: Partial<Pick<Phone, "name" | "connection_url" | "spoofed_model" | "env_vars">>
): Promise<Phone> {
  const data = await apiFetch<SingleResponse>(`/api/phones/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.phone;
}

export async function deletePhone(id: string): Promise<void> {
  await apiFetch(`/api/phones/${id}`, { method: "DELETE" });
}

export async function rebootPhone(id: string): Promise<{ success: boolean; instant: boolean }> {
  return apiFetch<{ success: boolean; instant: boolean }>(`/api/phones/${id}/reboot`, {
    method: "POST",
  });
}

// ---- OCR debug ----
// Items renvoyés par Vision (cf. VFVisionService.h §protocole) :
// coords normalisées [0..1] avec origine top-left, comme côté Apple Vision
// après inversion du Y faite par le tweak.
export interface OCRItem {
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
}

interface OCRResponse {
  success: boolean;
  items: OCRItem[];
  request_id?: string;
}

export async function ocrSnapshot(id: string): Promise<OCRItem[]> {
  const data = await apiFetch<OCRResponse>(`/api/phones/${id}/ocr`, {
    method: "POST",
  });
  return data.items;
}
